import { Button, IconButton, Card, Container, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { EldDriver, EldVehicle } from "src/models";
import useModelData, { deleteById, replaceById } from "src/hooks/use-model-data";
import Iconify from "src/components/iconify/iconify";
import { DisplayPageData } from "src/components/display-page-data";
import useModelSavingData from "src/hooks/use-model-saving-data";

import PageHeader from "src/components/page-header";
import Label from "src/components/label";
import NoneFound from "src/components/none-found";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useRouter } from "src/routes/hooks";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import { useAuthContext } from "src/auth/hooks";
import { EldDriverModal } from "./_eld_driver_modal";
import { isIOS } from "../eld-driver-logs/single";


type EldDriversProps = {
  eldDrivers?: EldDriver[],
  onEldDriverSelected: (a: EldDriver) => void
}

const searchMapper = (eldDriver: EldDriver) => [eldDriver.suspended ? "Suspended" : ""];

const EldDrivers = memo(({ eldDrivers, onEldDriverSelected }: EldDriversProps) => {
  const { sortedList, sortData } = useSort(eldDrivers, "firstName");

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, ["firstName", "lastName", "username", "email", "driverNumber", "phone", "driverLicenseNumber", "driverLicenseState", "hosRule", "cargoType", "restBreak", "eldVehicleUnitNumber"], undefined, searchMapper);

  if (!sortedList) {
    return null;
  }

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="ELD Drivers" />

    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortKey="driverNumber" sortData={sortData} >
                Driver Number
              </SortableHeader>

              <SortableHeader sortKey="firstName" sortData={sortData} >
                Name
              </SortableHeader>

              <SortableHeader sortKey="username" sortData={sortData} >
                Username
              </SortableHeader>

              {/* <SortableHeader sortKey="email" sortData={sortData} >
                Email
              </SortableHeader> */}
              <SortableHeader sortKey="eldVehicleUnitNumber" sortData={sortData}>
                Assigned Vehicle
              </SortableHeader>

              <TruxTableCell align="right">
                Malfunction Letter
              </TruxTableCell>
            </TableRow>
          </TableHead>


          <TableBody>
            {filteredList.map((eldDriver, index) =>
            (<TableRow
              key={index}
              hover
              onClick={() => onEldDriverSelected(eldDriver)}
              sx={{ cursor: "pointer" }}
            >

              <TruxTableCell>
                {eldDriver.driverNumber}
              </TruxTableCell>

              <TruxTableCell>
                {eldDriver.firstName} {eldDriver.lastName}
                {eldDriver.suspended && <Label sx={{ ml: 1, color: "white", background: "#29b6f6" }}>Suspended</Label>}
              </TruxTableCell>

              <TruxTableCell>
                {eldDriver.username}
              </TruxTableCell>

              {/* <TruxTableCell>
                {eldDriver.email}
              </TruxTableCell> */}

              <TruxTableCell>
                {eldDriver.eldVehicleUnitNumber}
              </TruxTableCell>

              <TruxTableCell align="right">
                <IconButton onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  axios({url:
                    formatEndpoint(endpoints.eldDrivers.generateMalfunctionLetter, {eldDriverId: eldDriver.id}),
                    method: 'GET',responseType: 'blob',
                  }).then(async (response) => {
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const getBase64 = () => new Promise<string>((resolve) => {
                      const reader = new FileReader();
                      reader.onloadend = () => resolve(reader.result as string);
                      reader.readAsDataURL(blob);
                    });

                    const base64String = await getBase64();

                    function base64ToArrayBuffer(_base64Str: string) {
                      const binaryString = window.atob(_base64Str);
                      const binaryLen = binaryString.length;
                      const bytes = new Uint8Array(binaryLen);
                      for (let i = 0; i < binaryLen; i+=1) {
                            const ascii = binaryString.charCodeAt(i);
                            bytes[i] = ascii;
                      }
                      return bytes;
                    }

                    function showDocument(_base64Str: string, _contentType: string) {
                          const byte = base64ToArrayBuffer(_base64Str);
                          const blobInner = new Blob([byte], { type: _contentType });
                          const blobUrl = URL.createObjectURL(blobInner);
                          if (isIOS()) {
                            const link = document.createElement('a');
                            link.href = blobUrl;
                            link.click();
                          } else {
                            window.open(blobUrl, "_blank");
                          }
                    }
                    showDocument(base64String.split("data:application/pdf;base64,").join(""), "application/pdf");
                  }).catch((e1) => {
                    console.error(e1);
                    enqueueSnackbar("Unexpected exception has occured. Please contact customer support.", {variant: "error"})
                  })
                }}
              >
                <Iconify icon="mage:file-download"/>
              </IconButton>
              </TruxTableCell>
            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModel = {
  eldDrivers?: EldDriver[],
  eldVehicles?: EldVehicle[],
}

export default function Page() {
  const authContext = useAuthContext();
  const canUpdateEldDrivers = authContext.session?.updateEldDrivers ?? false;
  const settings = useSettingsContext();
  const router = useRouter();
  const dataLoader = useCallback(() => ({
    eldDrivers: axios.get(endpoints.eldDrivers.findAll).then(response => response.data),
    eldVehicles: axios.get(endpoints.eldVehicles.findAll).then(response => response.data),
  }), []);
  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      alert(error);
    }
  }, [loading, error]);

  const [isOpen, setIsOpen] = useState(false);

  const selectedEldDriverModel = useModelSavingData<EldDriver>(
    endpoints.eldDrivers.update,
    endpoints.eldDrivers.create
  );

  const updateEldDriver = () => {
    selectedEldDriverModel.save().then(response => {
      if (response) {
        enqueueSnackbar("Successfully saved the Eld Driver");
        const eldDriver = response.data as EldDriver;

        if (!eldDriver.active) {
          updateData("eldDrivers")(deleteById(data!.eldDrivers!, eldDriver))
        } else {
          updateData("eldDrivers")(replaceById(data!.eldDrivers!, eldDriver));
        }

        setIsOpen(false);
      }
    })
  }

  const onEldDriverSelected = useCallback((a: EldDriver) => {
    selectedEldDriverModel.set({
      ...a, newPassword: "",
      newPasswordConfirm: ""
    });
    setIsOpen(true);
  }, [selectedEldDriverModel, setIsOpen]);


  const onNewEldDriverSelected = useCallback(() => {
    selectedEldDriverModel.set({
      id: 0,
      createdAt: undefined,
      active: true,
      suspended: false,
      organizationId: 0,

      externalId: "",

      username: "",
      email: "",

      newPassword: "",
      newPasswordConfirm: "",

      firstName: "",
      lastName: "",
      driverNumber: "",
      phone: "",
      driverLicenseNumber: "",
      driverLicenseState: "",

      eldVehicleId: 0,
      eldVehicleUnitNumber: "",
      eldVehicleVin: "",
      trailers: [],

      hasAllVehicles: false,
      eldVehicleIds: [],

      exemptDriver: false,
      hosRule: "",
      cargoType: "",
      restBreak: '30min',
      allowPersonalUse: false,
      allowYardMoves: false,
      canUseShortSbFirst: false,
      unlimitedTrailers: false,
      unlimitedShippingDocs: true,
      signatureUrl: "",
      loadHistEvents: true,
      loadUnidentifiedEvents: true,
      readValuesDirectly: true,
    });
    setIsOpen(true);
  }, [selectedEldDriverModel]);

  const newEldDriverButton = <Button
    onClick={onNewEldDriverSelected}
    variant="contained"
    color="success"
    startIcon={<Iconify icon="mingcute:add-line" />}
  >
    New Eld Driver
  </Button>;

const inactiveEldDriversButton = <Button
  onClick={() => router.push("/app/eld/eld-drivers/inactive")}
  variant="soft"
>
  Inactive ELD Drivers
</Button>;


  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && data?.eldDrivers?.length !== 0 &&
          <PageHeader title="ELD Drivers">
            {inactiveEldDriversButton}
            {canUpdateEldDrivers && newEldDriverButton}
          </PageHeader>
        }

        {data && data?.eldDrivers?.length === 0 &&
          <NoneFound title="No Active ELD Drivers Found" message="To add an Eld Driver click the 'New Eld Driver' button below.">
            {inactiveEldDriversButton}
            {canUpdateEldDrivers && newEldDriverButton}
          </NoneFound>
        }

        {data && data?.eldDrivers?.length !== 0 && <EldDrivers eldDrivers={data.eldDrivers} onEldDriverSelected={onEldDriverSelected} />}

        {data && <EldDriverModal
          selectedEldDriverModel={selectedEldDriverModel}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          updateEldDriver={updateEldDriver}
          eldVehicles={data.eldVehicles || []}
          canUpdateEldDrivers={canUpdateEldDrivers}
        />}
      </DisplayPageData>
    </Container >
  );
}
